import { useMemo, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { ReportPageShell } from "../../components/ui/reportPageShell";
import { FINE_REPORT } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { SelectField } from "../../components/ui/form-fields";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";

/**
 * Fine Report (FR-037) — late fines, short-duty deductions, and grace usage.
 */
const FineReport = () => {
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [view, setView] = useState("detail");
    const [penaltyType, setPenaltyType] = useState("all");

    const viewFilter = useMemo(() => ({ view, penalty_type: penaltyType }), [view, penaltyType]);

    const EmployeeCell = ({ row }) => (
        <div>
            <div className="font-medium text-gray-900 dark:text-white">{row?.employee_name || "-"}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{row?.employee_code || "-"}</div>
        </div>
    );

    const detailColumns = [
        { accessor: "employee_name", Header: "Employee", fixed: true, filterable: true, maxWidth: "260px", Cell: EmployeeCell },
        { accessor: "department", Header: "Department", filterable: true, Cell: ({ value }) => value ?? "-" },
        { accessor: "date", Header: "Date", Cell: ({ value }) => value ?? "-" },
        {
            accessor: "penalty_type", Header: "Type", filterable: true, Cell: ({ value }) => (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                    {value || "-"}
                </span>
            )
        },
        { accessor: "late_minutes", Header: "Late (min)", Cell: ({ value }) => value ?? "-" },
        { accessor: "grace_status", Header: "Grace Used", Cell: ({ value }) => value ?? "-" },
        {
            accessor: "penalty", Header: "Penalty", Cell: ({ value }) => (
                <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                    value === "Fine"
                        ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300")}>
                    {value || "-"}
                </span>
            )
        },
        { accessor: "value", Header: "Value", Cell: ({ value }) => value ?? "-" },
        {
            accessor: "review_status", Header: "Status", Cell: ({ value }) => (
                <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                    value === "Reviewed"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300")}>
                    {value || "-"}
                </span>
            )
        },
        { accessor: "reason", Header: "Reason", Cell: ({ value }) => value ?? "-" },
    ];

    const summaryColumns = [
        { accessor: "employee_name", Header: "Employee", fixed: true, filterable: true, maxWidth: "260px", Cell: EmployeeCell },
        { accessor: "department", Header: "Department", filterable: true, Cell: ({ value }) => value ?? "-" },
        { accessor: "period", Header: "Period", Cell: ({ value }) => value ?? "-" },
        { accessor: "penalty_days", Header: "Penalty Days", Cell: ({ value }) => value ?? 0 },
        { accessor: "late_days", Header: "Late Days", Cell: ({ value }) => value ?? 0 },
        { accessor: "short_duty_days", Header: "Short-duty Days", Cell: ({ value }) => value ?? 0 },
        { accessor: "deduction_days", Header: "Deduction Days", Cell: ({ value }) => value ?? 0 },
        {
            accessor: "fine_total", Header: "Fine Total", Cell: ({ value }) => (
                <span className="font-medium text-rose-600 dark:text-rose-400">₹{Number(value || 0).toLocaleString("en-IN")}</span>
            )
        },
        { accessor: "reviewed_days", Header: "Reviewed", Cell: ({ value }) => value ?? 0 },
        { accessor: "pending_days", Header: "Pending", Cell: ({ value }) => value ?? 0 },
    ];

    return (
        <ReportPageShell
            title="Fine Report"
            count={totalRecords}
            headerExtra={(
                <div className="flex flex-wrap items-center gap-2">
                    <SelectField
                        size="sm"
                        value={penaltyType}
                        onChange={(e) => setPenaltyType(e.target.value)}
                        className="w-auto min-w-[10rem]"
                    >
                        <option value="all">All penalties</option>
                        <option value="late">Late only</option>
                        <option value="short_duty">Short-duty only</option>
                    </SelectField>

                    <SegmentSwitch
                        options={[
                            { value: "detail", label: "Detail" },
                            { value: "summary", label: "Summary" },
                        ]}
                        value={view}
                        onChange={setView}
                    />
                </div>
            )}
        >
            <Reactable
                columns={view === "summary" ? summaryColumns : detailColumns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={FINE_REPORT}
                serverSide={true}
                serverTrigger={`${view}-${penaltyType}`}
                filter={viewFilter}
                printFilter={viewFilter}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isMonthYearRequired={true}
            />
        </ReportPageShell>
    );
};

export default FineReport;
